import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { signup } from '../utils/api';
import { toast } from 'sonner';
import { initiateGoogleLogin } from '../utils/auth';
import OAuthLoadingModal from '@/components/OAuthLoadingModal';

// Indian states and their capitals
const INDIAN_STATES = {
  "Andhra Pradesh": "Amaravati",
  "Arunachal Pradesh": "Itanagar",
  "Assam": "Dispur",
  "Bihar": "Patna",
  "Chhattisgarh": "Raipur",
  "Goa": "Panaji",
  "Gujarat": "Gandhinagar",
  "Haryana": "Chandigarh",
  "Himachal Pradesh": "Shimla",
  "Jharkhand": "Ranchi",
  "Karnataka": "Bengaluru",
  "Kerala": "Thiruvananthapuram",
  "Madhya Pradesh": "Bhopal",
  "Maharashtra": "Mumbai",
  "Manipur": "Imphal",
  "Meghalaya": "Shillong",
  "Mizoram": "Aizawl",
  "Nagaland": "Kohima",
  "Odisha": "Bhubaneswar",
  "Punjab": "Chandigarh",
  "Rajasthan": "Jaipur",
  "Sikkim": "Gangtok",
  "Tamil Nadu": "Chennai",
  "Telangana": "Hyderabad",
  "Tripura": "Agartala",
  "Uttar Pradesh": "Lucknow",
  "Uttarakhand": "Dehradun",
  "West Bengal": "Kolkata",
  "Andaman and Nicobar Islands": "Port Blair",
  "Chandigarh": "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu": "Daman",
  "Delhi": "New Delhi",
  "Jammu and Kashmir": "Srinagar",
  "Ladakh": "Leh",
  "Lakshadweep": "Kavaratti",
  "Puducherry": "Puducherry"
};

const SignupPage = ({ setUser }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [sameAsPhone, setSameAsPhone] = useState(false);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const navigate = useNavigate();

  const handleSameAsPhoneChange = (e) => {
    const checked = e.target.checked;
    setSameAsPhone(checked);
    if (checked) {
      setWhatsappNumber(phone);
    } else {
      setWhatsappNumber('');
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (sameAsPhone) {
      setWhatsappNumber(value);
    }
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setState(selectedState);
    // Auto-fill capital city
    if (selectedState && INDIAN_STATES[selectedState]) {
      setCity(INDIAN_STATES[selectedState]);
    } else {
      setCity('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const response = await signup({ 
        name: fullName,
        first_name: firstName,
        last_name: lastName,
        email, 
        phone: `${countryCode}${phone}`,
        country_code: countryCode,
        whatsapp_number: whatsappNumber ? `${countryCode}${whatsappNumber}` : null,
        state,
        city,
        password 
      });
      setUser(response.data.user);
      toast.success('Account created successfully!');
      navigate('/member-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4 sm:px-6 py-6 sm:py-8">
      <div className="w-full max-w-2xl">
        {/* Back Button */}
        <Button 
          onClick={() => navigate('/')}
          className="mb-4 sm:mb-6 border border-black bg-white text-black hover:bg-black hover:text-white transition-all text-sm sm:text-base min-h-[44px]"
        >
          \u2190 Back
        </Button>
        
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/jb59wbkx_THC%20Final%20Logo%20%28Cutout%202%29.png" 
              alt="The Haute Circle" 
              className="h-32 sm:h-40 mx-auto mb-4 sm:mb-6" 
            />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome to The Haute Circle</h1>
          <p className="text-gray-600 text-xs sm:text-sm">Log-in/Sign-up to proceed</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">First Name*</label>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  placeholder="Enter first name"
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Last Name*</label>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  placeholder="Enter last name"
                  className="min-h-[44px]"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2">Email ID*</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter email address"
                className="min-h-[44px]"
              />
            </div>

            {/* Phone Number with Country Code */}
            <div>
              <label className="block text-sm font-medium mb-2">Phone Number*</label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="w-20 sm:w-24 rounded-md border border-gray-300 px-2 sm:px-3 py-2 text-sm min-h-[44px]"
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+971">+971</option>
                  <option value="+65">+65</option>
                </select>
                <Input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  required
                  placeholder="Enter phone number"
                  className="flex-1 min-h-[44px]"
                />
              </div>
            </div>

            {/* WhatsApp Number */}
            <div>
              <label className="block text-sm font-medium mb-2">WhatsApp Number</label>
              <Input
                type="tel"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="Enter WhatsApp number"
                disabled={sameAsPhone}
                className="min-h-[44px]"
              />
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="sameAsPhone"
                  checked={sameAsPhone}
                  onChange={handleSameAsPhoneChange}
                  className="w-5 h-5 text-gold border-gray-300 rounded focus:ring-gold"
                />
                <label htmlFor="sameAsPhone" className="ml-2 text-sm text-gray-600">
                  Same as phone number
                </label>
              </div>
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium mb-2">State*</label>
              <select
                value={state}
                onChange={handleStateChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[44px]"
              >
                <option value="">Select State</option>
                {Object.keys(INDIAN_STATES).map((stateName) => (
                  <option key={stateName} value={stateName}>{stateName}</option>
                ))}
              </select>
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium mb-2">Nearest City*</label>
              <Input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                placeholder="Capital city (auto-filled based on state)"
                readOnly
                className="min-h-[44px]"
              />
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Password*</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  placeholder="Enter password"
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Re-enter Password*</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength="6"
                  placeholder="Re-enter password"
                  className="min-h-[44px]"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-black text-white hover:bg-black/90 mt-6 min-h-[48px] text-sm sm:text-base" 
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </Button>
          </form>

          <div className="mt-4 sm:mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In */}
            <Button 
              onClick={() => {
                setShowOAuthModal(true);
                setTimeout(() => {
                  initiateGoogleLogin();
                }, 500);
              }}
              className="w-full mt-4 border border-gray-300 bg-white text-black hover:bg-gray-50 flex items-center justify-center min-h-[48px] text-sm sm:text-base"
            >
              <svg className="w-5 h-5 mr-2 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm sm:text-base">Continue with Google</span>
            </Button>
            
            {/* OAuth Loading Modal */}
            <OAuthLoadingModal 
              isOpen={showOAuthModal}
              onClose={() => setShowOAuthModal(false)}
              provider="Google"
            />
          </div>

          <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-gold hover:underline font-medium">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
